import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { onAuthStateChanged, reauthenticateWithCredential, reauthenticateWithPopup, GoogleAuthProvider, EmailAuthProvider } from 'firebase/auth';
import { collection, doc, onSnapshot, addDoc, updateDoc, deleteDoc, writeBatch, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { PlusCircle, Lock, Unlock, LogIn, Trash2, Upload, Download, KeyRound } from 'lucide-react';
import * as XLSX from 'xlsx';

import { auth, db } from '../../firebase';

import { AuthModal, ChangePinModal, CryptoSetupModal, PinRecoveryModal } from './authComponents';
import { GiftCardTable, StoreSummaryTable, CardForm } from './tableComponents';
import { SummaryCards, LoadingSpinner, FirebaseConfigWarning, SearchInput, ConfirmationModal, ImportModal } from './uiComponents';
import { Button, IconButton } from '../../components/ui';

import { cryptoHelpers } from './crypto';
import AnalyticsDashboard from './AnalyticsDashboard';

const appId = 'gift-card-manager-v1';

// Resolve the comparable value for a given sort key. Numeric columns return
// numbers, text columns return lowercased strings so comparisons are stable
// and case-insensitive. Used by the multi-column sort below.
const getSortValue = (card, key) => {
    switch (key) {
        case 'retailer': return (card.retailer || '').toLowerCase();
        case 'value': return card.initialValue;
        case 'pricePaid': return card.pricePaid;
        case 'percentOff': return card.initialValue > 0 ? ((card.initialValue - card.pricePaid) / card.initialValue) * 100 : 0;
        case 'spent': return card.spent;
        case 'remaining': return card.initialValue - card.spent;
        case 'source': return (card.source || '').toLowerCase();
        case 'notes': return (card.notes || '').toLowerCase();
        case 'createdAt':
        default: return card.createdAt || '0';
    }
};

export default function Manager() {
    // --- State ---
    const [user, setUser] = useState(null);
    const [isAuthReady, setIsAuthReady] = useState(false);

    const [cryptoKey, setCryptoKey] = useState(null);
    const [isLocked, setIsLocked] = useState(true);
    const [cryptoSettings, setCryptoSettings] = useState(null);
    const [showCryptoModal, setShowCryptoModal] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [showChangePinModal, setShowChangePinModal] = useState(false);

    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [cardToDelete, setCardToDelete] = useState(null);

    const [revealedCardIds, setRevealedCardIds] = useState([]);
    const [giftCards, setGiftCards] = useState([]);
    const [decryptedCards, setDecryptedCards] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isDecrypting, setIsDecrypting] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [editingCardId, setEditingCardId] = useState(null);
    // Ordered list of active sort criteria, highest priority first. Empty means
    // the default (newest-first) ordering applied in `displayCards` below.
    const [sortConfig, setSortConfig] = useState([]);
    const [retailerFilter, setRetailerFilter] = useState('');
    const [debouncedRetailerFilter, setDebouncedRetailerFilter] = useState('');
    const [retailerNames, setRetailerNames] = useState([]);

    const [barcodeCardIds, setBarcodeCardIds] = useState([]);

    const [isRecoveringPin, setIsRecoveringPin] = useState(false);
    const [recoveredDataKey, setRecoveredDataKey] = useState(null);

    const [showImportModal, setShowImportModal] = useState(false);
    const [selectedCardIds, setSelectedCardIds] = useState(new Set());


    // Wrapped in useCallback (with only stable setState deps) so the auth
    // effect below can list it as a dependency without re-subscribing on
    // every render. Must be declared BEFORE that effect (the dependency
    // array is evaluated during render).
    const handleLock = useCallback((isLogout = false) => {
        sessionStorage.removeItem('sessionCryptoKey');
        setCryptoKey(null);
        setIsLocked(true);
        setRevealedCardIds([]);
        setBarcodeCardIds([]); // Clear the array on lock
        if (!isLogout) setShowCryptoModal(true);
    }, []);

    // --- Firebase & Auth Initialization ---
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setUser(user);
            setIsAuthReady(true);
            if (!user) handleLock(true);
        });
        return () => unsubscribe();
    }, [handleLock]);

    // --- Fetch all retailer names for the autocomplete feature ---
    useEffect(() => {
        const fetchRetailerNames = async () => {
            if (!db) return;
            try {
                const retailersCol = collection(db, 'retailers');
                const snapshot = await getDocs(retailersCol);
                const names = snapshot.docs.map(doc => doc.data().name);
                setRetailerNames(names.sort());
            } catch (error) {
                console.error("Error fetching retailer names:", error);
            }
        };
        fetchRetailerNames();
    }, []);


    // --- Session-based Unlock Check ---
    useEffect(() => {
        const checkSession = async () => {
            try {
                const sessionKey = sessionStorage.getItem('sessionCryptoKey');
                if (sessionKey && user) {
                    const key = await window.crypto.subtle.importKey('jwk', JSON.parse(sessionKey), { name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
                    setCryptoKey(key);
                    setIsLocked(false);
                }
            } catch (e) {
                console.error("Failed to restore session key", e);
                sessionStorage.removeItem('sessionCryptoKey');
            }
        };
        checkSession();
    }, [user]);

    // --- Crypto Settings & Data Fetching ---
    useEffect(() => {
        if (!isAuthReady || !db || !user) {
            if (isAuthReady && !user) setShowAuthModal(true);
            return;
        };

        setShowAuthModal(false);
        const settingsDocRef = doc(db, `users/${user.uid}/settings/crypto`);
        const unsubscribeSettings = onSnapshot(settingsDocRef, (docSnap) => {
            if (docSnap.exists()) {
                const settings = docSnap.data();
                settings.salt = new Uint8Array(atob(settings.salt).split('').map(c => c.charCodeAt(0)));
                setCryptoSettings(settings);
            } else {
                setCryptoSettings(null);
            }
            if (isLocked) setShowCryptoModal(true);
        }, (error) => { console.error("Error fetching crypto settings:", error); });

        const collectionPath = `users/${user.uid}/apps/${appId}/gift_cards`;
        const giftCardsCollection = collection(db, collectionPath);
        setIsLoading(true);
        const unsubscribeCards = onSnapshot(giftCardsCollection, (snapshot) => {
            const cardsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), initialValue: parseFloat(doc.data().initialValue) || 0, pricePaid: parseFloat(doc.data().pricePaid) || 0, spent: parseFloat(doc.data().spent) || 0 }));
            setGiftCards(cardsData);
            setIsLoading(false);
        }, (error) => { console.error("Error fetching gift cards:", error); setIsLoading(false); });

        return () => {
            unsubscribeSettings();
            unsubscribeCards();
        };
    }, [isAuthReady, user, isLocked]);

    // --- Decryption with caching ---
    const decryptionCache = useRef(new Map()); // Cache: encryptedCode -> decryptedCode

    useEffect(() => {
        if (cryptoKey && giftCards.length > 0) {
            let isCancelled = false;
            setIsDecrypting(true);

            const decryptAll = async () => {
                const decrypted = await Promise.all(giftCards.map(async (card) => {
                    // Check cache first to avoid redundant decryption
                    let decryptedCode = decryptionCache.current.get(card.code);
                    let decryptedPin = decryptionCache.current.get(card.pin);

                    if (decryptedCode === undefined && card.code) {
                        decryptedCode = await cryptoHelpers.decrypt(card.code, cryptoKey);
                        decryptionCache.current.set(card.code, decryptedCode);
                    }
                    if (decryptedPin === undefined && card.pin) {
                        decryptedPin = await cryptoHelpers.decrypt(card.pin, cryptoKey);
                        decryptionCache.current.set(card.pin, decryptedPin);
                    }

                    return {
                        ...card,
                        code: decryptedCode ?? card.code ?? '',
                        pin: decryptedPin ?? ''
                    };
                }));

                if (!isCancelled) {
                    setDecryptedCards(decrypted);
                    setIsDecrypting(false);
                }
            };
            decryptAll();

            return () => { isCancelled = true; };
        } else if (!cryptoKey) {
            setDecryptedCards(giftCards.map(c => ({ ...c, code: 'LOCKED', pin: 'LOCKED' })));
            setIsDecrypting(false);
        } else if (giftCards.length === 0) {
            setDecryptedCards([]);
            setIsDecrypting(false);
        }
    }, [cryptoKey, giftCards]);

    // --- Debounce search filter for performance ---
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedRetailerFilter(retailerFilter);
        }, 150);
        return () => clearTimeout(timer);
    }, [retailerFilter]);

    // --- Filtering & Sorting ---
    const displayCards = useMemo(() => {
        let filteredCards = decryptedCards;

        if (debouncedRetailerFilter) {
            filteredCards = decryptedCards.filter(card =>
                card.retailer.toLowerCase().includes(debouncedRetailerFilter.toLowerCase())
            );
        }

        // Apply each active criterion in priority order; with no explicit sort
        // fall back to newest-first so the initial view is unchanged.
        const activeSort = sortConfig.length > 0 ? sortConfig : [{ key: 'createdAt', direction: 'desc' }];
        let sortableCards = [...filteredCards];
        sortableCards.sort((a, b) => {
            for (const { key, direction } of activeSort) {
                const aValue = getSortValue(a, key);
                const bValue = getSortValue(b, key);
                if (aValue < bValue) return direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
        return sortableCards;
    }, [decryptedCards, sortConfig, debouncedRetailerFilter]);

    // --- Handlers ---
    const handleUnlock = async (key) => {
        const exportableKey = await window.crypto.subtle.exportKey('jwk', key);
        sessionStorage.setItem('sessionCryptoKey', JSON.stringify(exportableKey));
        setCryptoKey(key);
        setIsLocked(false);
        setShowCryptoModal(false);
    };

    const handleAddCard = async (cardData) => {
        if (!db || !user || !cryptoKey) return;
        const encryptedCode = await cryptoHelpers.encrypt(cardData.code || '', cryptoKey);
        const encryptedPin = await cryptoHelpers.encrypt(cardData.pin || '', cryptoKey);
        const collectionPath = `users/${user.uid}/apps/${appId}/gift_cards`;
        try {
            await addDoc(collection(db, collectionPath), {
                ...cardData,
                createdAt: new Date().toISOString(),
                retailer_lowercase: cardData.retailer.toLowerCase(),
                code: encryptedCode,
                pin: encryptedPin,
                initialValue: Number(cardData.initialValue),
                pricePaid: Number(cardData.pricePaid),
                spent: Number(cardData.spent)
            });
            setIsAdding(false);
        } catch (error) { console.error("Error adding card:", error); }
    };

    const handleUpdateCard = async (cardId, updatedData) => {
        if (!db || !user || !cryptoKey) return;
        const dataToUpdate = { ...updatedData, retailer_lowercase: updatedData.retailer.toLowerCase() };
        if (updatedData.code) dataToUpdate.code = await cryptoHelpers.encrypt(updatedData.code, cryptoKey);
        if (updatedData.pin) dataToUpdate.pin = await cryptoHelpers.encrypt(updatedData.pin, cryptoKey);
        const docPath = `users/${user.uid}/apps/${appId}/gift_cards/${cardId}`;
        try {
            await updateDoc(doc(db, docPath), dataToUpdate);
            setEditingCardId(null);
        } catch (error) { console.error("Error updating card:", error); }
    };

    const handleDeleteClick = (cardId) => {
        setSelectedCardIds(new Set());
        setCardToDelete(cardId);
        setShowDeleteConfirm(true);
    };

    const handleDeleteCard = async () => {
        if (!db || !user || !cardToDelete) return;
        const docPath = `users/${user.uid}/apps/${appId}/gift_cards/${cardToDelete}`;
        try {
            await deleteDoc(doc(db, docPath));
        } catch (error) {
            console.error("Error deleting card:", error);
        } finally {
            setShowDeleteConfirm(false);
            setCardToDelete(null);
        }
    };

    const handleChangePin = async (currentPin, newPin) => {
        if (!cryptoKey || !db || !user || !cryptoSettings) throw new Error("Not ready to change PIN.");

        // 1. Verify the current PIN
        const oldPinDerivedKey = await cryptoHelpers.deriveKey(currentPin, cryptoSettings.salt);
        const decryptedCheck = await cryptoHelpers.decrypt(cryptoSettings.keyCheck, oldPinDerivedKey);
        if (decryptedCheck !== 'key_check_ok') throw new Error("Incorrect current PIN.");

        // 2. Prepare new crypto materials
        const newSalt = window.crypto.getRandomValues(new Uint8Array(16));
        const newPinDerivedKey = await cryptoHelpers.deriveKey(newPin, newSalt);
        const newKeyCheck = await cryptoHelpers.encrypt('key_check_ok', newPinDerivedKey);

        // 3. Handle migration for legacy users vs. update for new users
        if (!cryptoSettings.encryptedDataKey) {
            // LEGACY USER MIGRATION: Re-encrypt all cards with a new dataKey
            const newDataKey = await cryptoHelpers.generateDataKey();
            const newEncryptedDataKey = await cryptoHelpers.encrypt(
                JSON.stringify(await window.crypto.subtle.exportKey('jwk', newDataKey)),
                newPinDerivedKey
            );

            const batch = writeBatch(db);
            for (const card of giftCards) {
                const decryptedCode = await cryptoHelpers.decrypt(card.code, oldPinDerivedKey);
                const decryptedPin = await cryptoHelpers.decrypt(card.pin, oldPinDerivedKey);
                const reEncryptedCode = await cryptoHelpers.encrypt(decryptedCode || '', newDataKey);
                const reEncryptedPin = await cryptoHelpers.encrypt(decryptedPin || '', newDataKey);
                const cardDocRef = doc(db, `users/${user.uid}/apps/${appId}/gift_cards/${card.id}`);
                batch.update(cardDocRef, { code: reEncryptedCode, pin: reEncryptedPin });
            }

            const settingsDocRef = doc(db, `users/${user.uid}/settings/crypto`);
            batch.update(settingsDocRef, {
                salt: btoa(String.fromCharCode.apply(null, newSalt)),
                keyCheck: newKeyCheck,
                encryptedDataKey: newEncryptedDataKey,
            });

            await batch.commit();
            await handleUnlock(newDataKey);

        } else {
            // NEW USER UPDATE: Just re-encrypt the existing dataKey
            const newEncryptedDataKey = await cryptoHelpers.encrypt(
                JSON.stringify(await window.crypto.subtle.exportKey('jwk', cryptoKey)),
                newPinDerivedKey
            );

            const settingsDocRef = doc(db, `users/${user.uid}/settings/crypto`);
            await updateDoc(settingsDocRef, {
                salt: btoa(String.fromCharCode.apply(null, newSalt)),
                keyCheck: newKeyCheck,
                encryptedDataKey: newEncryptedDataKey,
            });
        }

        setShowChangePinModal(false);
    };

    // Click-to-sort: cycle a column through its two directions, then off.
    // Text columns start A→Z; numeric columns start high→low (the more useful
    // default for balances/discounts). Clicking additional columns layers them
    // as lower-priority tie-breakers so several can be active at once.
    const handleSort = useCallback((key, type) => {
        const firstDirection = type === 'number' ? 'desc' : 'asc';
        const secondDirection = firstDirection === 'asc' ? 'desc' : 'asc';
        setSortConfig(prev => {
            const existing = prev.find(s => s.key === key);
            if (!existing) return [...prev, { key, direction: firstDirection }];
            if (existing.direction === firstDirection) {
                return prev.map(s => s.key === key ? { ...s, direction: secondDirection } : s);
            }
            // Third click removes this column from the sort.
            return prev.filter(s => s.key !== key);
        });
    }, []);

    const toggleRevealCard = (cardId) => setRevealedCardIds(prev => prev.includes(cardId) ? prev.filter(id => id !== cardId) : [...prev, cardId]);

    const handleGenerateBarcode = (card) => {
        setBarcodeCardIds(prevIds => {
            if (prevIds.includes(card.id)) {
                return prevIds.filter(id => id !== card.id);
            } else {
                return [...prevIds, card.id];
            }
        });
    };

    const handleCardSelect = (cardId) => {
        setSelectedCardIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(cardId)) {
                newSet.delete(cardId);
            } else {
                newSet.add(cardId);
            }
            return newSet;
        });
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            const allCardIds = new Set(displayCards.map(card => card.id));
            setSelectedCardIds(allCardIds);
        } else {
            setSelectedCardIds(new Set());
        }
    };

    const handleBulkDelete = async () => {
        if (!db || !user || selectedCardIds.size === 0) return;

        const batch = writeBatch(db);
        selectedCardIds.forEach(cardId => {
            const docRef = doc(db, `users/${user.uid}/apps/${appId}/gift_cards/${cardId}`);
            batch.delete(docRef);
        });

        try {
            await batch.commit();
            setSelectedCardIds(new Set());
        } catch (error) {
            console.error("Error deleting cards in bulk:", error);
        } finally {
            setShowDeleteConfirm(false);
        }
    };

    const handleExportToExcel = () => {
        if (isLocked || decryptedCards.length === 0) return;

        // Export the decrypted cards so the spreadsheet is usable as a backup
        const dataForExport = decryptedCards.map(card => ({
            Retailer: card.retailer,
            Value: card.initialValue,
            Paid: card.pricePaid,
            Spent: card.spent,
            Code: card.code,
            PIN: card.pin,
            Source: card.source || '',
            Notes: card.notes || ''
        }));

        const worksheet = XLSX.utils.json_to_sheet(dataForExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Gift Cards");

        const date = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(workbook, `GiftScout_Export_${date}.xlsx`);
    };

    // --- PIN RECOVERY LOGIC ---
    const handleInitiatePinRecovery = async () => {
        if (!user) throw new Error("User not signed in.");

        const providerId = user.providerData[0]?.providerId;

        try {
            // 1. Re-authenticate the user
            if (providerId === 'password') {
                const password = prompt("To recover your PIN, please re-enter your account password:");
                if (!password) return; // User cancelled
                const credential = EmailAuthProvider.credential(user.email, password);
                await reauthenticateWithCredential(user, credential);
            } else if (providerId === 'google.com') {
                const provider = new GoogleAuthProvider();
                await reauthenticateWithPopup(user, provider);
            } else {
                throw new Error(`Unsupported auth provider: ${providerId}`);
            }

            // 2. Call the Cloud Function
            const functions = getFunctions();
            const recoverDataKey = httpsCallable(functions, 'recoverDataKey');
            const result = await recoverDataKey();

            // 3. The result contains the decrypted dataKey's JWK
            const { dataKeyJwk } = result.data;
            if (!dataKeyJwk) {
                throw new Error("Backend did not return a valid data key.");
            }

            // 4. Import the key and store it in state
            const recoveredKey = await window.crypto.subtle.importKey(
                'jwk',
                JSON.parse(dataKeyJwk),
                { name: 'AES-GCM' },
                true,
                ['encrypt', 'decrypt']
            );
            setRecoveredDataKey(recoveredKey);

        } catch (error) {
            console.error("PIN Recovery Error:", error);
            const errorMessage = error.code === 'auth/wrong-password'
                ? "Incorrect password. Re-authentication failed."
                : `Could not start recovery process. Error: ${error.message}`;
            throw new Error(errorMessage);
        }
    };

    const handleSetNewPinAfterRecovery = async (newPin) => {
        if (!recoveredDataKey) {
            throw new Error("No recovered data key found.");
        }
        const newSalt = window.crypto.getRandomValues(new Uint8Array(16));
        const newPinDerivedKey = await cryptoHelpers.deriveKey(newPin, newSalt);
        const dataKeyJwk = await window.crypto.subtle.exportKey('jwk', recoveredDataKey);

        const newEncryptedDataKey = await cryptoHelpers.encrypt(
            JSON.stringify(dataKeyJwk),
            newPinDerivedKey
        );
        const newKeyCheck = await cryptoHelpers.encrypt('key_check_ok', newPinDerivedKey);

        const settingsDocRef = doc(db, `users/${user.uid}/settings/crypto`);
        await updateDoc(settingsDocRef, {
            salt: btoa(String.fromCharCode.apply(null, newSalt)),
            keyCheck: newKeyCheck,
            encryptedDataKey: newEncryptedDataKey
        });

        await handleUnlock(recoveredDataKey);
        setIsRecoveringPin(false);
        setRecoveredDataKey(null);
    };

    const handleImportCards = async (cardsToImport) => {
        if (!db || !user || !cryptoKey || cardsToImport.length === 0) return;

        const batch = writeBatch(db);
        const collectionPath = `users/${user.uid}/apps/${appId}/gift_cards`;

        try {
            for (const cardData of cardsToImport) {
                const encryptedCode = await cryptoHelpers.encrypt(cardData.code || '', cryptoKey);
                const encryptedPin = await cryptoHelpers.encrypt(cardData.pin || '', cryptoKey);

                const newCardRef = doc(collection(db, collectionPath));

                batch.set(newCardRef, {
                    ...cardData,
                    createdAt: new Date().toISOString(),
                    retailer_lowercase: cardData.retailer.toLowerCase(),
                    code: encryptedCode,
                    pin: encryptedPin,
                    initialValue: Number(cardData.initialValue),
                    pricePaid: Number(cardData.pricePaid),
                    spent: Number(cardData.spent)
                });
            }

            await batch.commit();
            alert(`${cardsToImport.length} cards were successfully imported!`);

        } catch (error) {
            console.error("Error importing cards in bulk:", error);
            alert(`An error occurred during the import. Please check the console for details.`);
        }
    };

    const summary = useMemo(() => {
        const totalInitialValue = giftCards.reduce((sum, card) => sum + card.initialValue, 0);
        const totalSpentOnCards = giftCards.reduce((sum, card) => sum + card.spent, 0);
        const totalRemaining = totalInitialValue - totalSpentOnCards;
        const totalPaid = giftCards.reduce((sum, card) => sum + card.pricePaid, 0);
        const totalSavings = totalInitialValue - totalPaid;
        const storeSummary = giftCards.reduce((acc, card) => {
            if (!acc[card.retailer]) acc[card.retailer] = { spent: 0, remaining: 0, paid: 0, value: 0 };
            acc[card.retailer].spent += card.spent;
            acc[card.retailer].remaining += (card.initialValue - card.spent);
            acc[card.retailer].paid += card.pricePaid;
            acc[card.retailer].value += card.initialValue;
            return acc;
        }, {});
        return { totalSpentOnCards, totalRemaining, totalSavings, totalPaid, storeSummary };
    }, [giftCards]);

    if (!isAuthReady) return <LoadingSpinner />;

    const isFirebaseConfigured = process.env.REACT_APP_API_KEY && !process.env.REACT_APP_API_KEY.includes("YOUR_API_KEY");
    if (!isFirebaseConfigured) return <FirebaseConfigWarning />

    return (
        <>
            {/* --- Modals --- */}
            <ImportModal
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                onImport={handleImportCards}
            />
            {showAuthModal && !user && auth && <AuthModal auth={auth} />}
            {showCryptoModal && isLocked && user && db && <CryptoSetupModal settings={cryptoSettings} db={db} userId={user.uid} onUnlock={handleUnlock} cryptoHelpers={cryptoHelpers} onForgotPassword={() => setIsRecoveringPin(true)} />}
            {isRecoveringPin && <PinRecoveryModal onClose={() => setIsRecoveringPin(false)} onRecover={handleInitiatePinRecovery} onSetNewPin={handleSetNewPinAfterRecovery} />}
            {showChangePinModal && !isLocked && user && db && cryptoKey && <ChangePinModal onClose={() => setShowChangePinModal(false)} onChangePin={handleChangePin} />}
            <ConfirmationModal
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={cardToDelete ? handleDeleteCard : handleBulkDelete}
                title="Confirm Deletion"
                confirmText="Delete"
                isDangerous
                message={`${cardToDelete
                    ? 'Are you sure you want to delete this gift card?'
                    : `Are you sure you want to delete the ${selectedCardIds.size} selected gift cards?`
                    } This action cannot be undone.`}
            />

            {/* --- Main Page Content --- */}
            <div className="bg-gray-50 dark:bg-slate-900 min-h-screen overflow-x-clip transition-colors">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <SummaryCards summary={summary} />

                    <div className="mt-8 bg-white dark:bg-slate-800 p-4 sm:p-6 rounded-xl shadow-lg transition-colors duration-200">
                        <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-4">
                            <h2 className="text-2xl font-bold text-gray-700 dark:text-white">Your Gift Cards</h2>
                            <div className="flex flex-col sm:flex-row items-center gap-2 w-full sm:w-auto">
                                {user && !isLocked && selectedCardIds.size > 0 ? (
                                    <Button variant="danger" icon={Trash2} className="w-full sm:w-auto" onClick={() => { setCardToDelete(null); setShowDeleteConfirm(true); }}>
                                        Delete ({selectedCardIds.size})
                                    </Button>
                                ) : (
                                    <SearchInput value={retailerFilter} onChange={setRetailerFilter} />
                                )}

                                {user && !isLocked && (
                                    <>
                                        <Button icon={PlusCircle} className="w-full sm:w-auto sm:min-w-[180px]" onClick={() => setIsAdding(!isAdding)}>
                                            {isAdding ? 'Cancel' : 'Add New Card'}
                                        </Button>
                                        <div className="flex items-center gap-1">
                                            <IconButton icon={Upload} variant="brand" label="Import cards from CSV" onClick={() => setShowImportModal(true)} />
                                            <IconButton icon={Download} variant="brand" label="Export cards to Excel" onClick={handleExportToExcel} disabled={decryptedCards.length === 0} />
                                            <IconButton icon={KeyRound} variant="brand" label="Change PIN" onClick={() => setShowChangePinModal(true)} />
                                            <IconButton icon={Lock} variant="warn" label="Lock your vault" onClick={() => handleLock()} />
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                        {user && isLocked && (
                            <div className="text-center py-8 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg">
                                <Lock className="mx-auto h-12 w-12 text-yellow-500 dark:text-yellow-400" />
                                <h3 className="mt-2 text-lg font-medium text-yellow-800 dark:text-yellow-200">Your Vault is Locked</h3>
                                <p className="mt-1 text-sm text-yellow-600 dark:text-yellow-300">Enter your PIN to decrypt and view your gift cards.</p>
                                <Button icon={Unlock} className="mt-4" onClick={() => setShowCryptoModal(true)}>Unlock Vault</Button>
                            </div>
                        )}
                        {!user && (
                            <div className="text-center py-8 bg-gray-100 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg">
                                <LogIn className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-300" />
                                <h3 className="mt-2 text-lg font-medium text-gray-800 dark:text-white">Please Sign In</h3>
                                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Sign in to manage your gift cards.</p>
                            </div>
                        )}
                        {isAdding && !isLocked && <CardForm onSave={handleAddCard} onCancel={() => setIsAdding(false)} retailerNames={retailerNames} />}
                        {(isLoading || isDecrypting) ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 dark:border-green-400 mb-4"></div>
                                <p className="text-gray-500 dark:text-gray-400 text-sm">
                                    {isDecrypting ? 'Decrypting your cards...' : 'Loading...'}
                                </p>
                            </div>
                        ) : (
                            user && <div>
                                <GiftCardTable
                                    cards={displayCards}
                                    sortConfig={sortConfig}
                                    onSort={handleSort}
                                    editingId={editingCardId}
                                    onEdit={setEditingCardId}
                                    onSave={handleUpdateCard}
                                    onDelete={handleDeleteClick}
                                    isLocked={isLocked}
                                    revealedCardIds={revealedCardIds}
                                    onReveal={toggleRevealCard}
                                    retailerNames={retailerNames}
                                    onGenerateBarcode={handleGenerateBarcode}
                                    barcodeCardIds={barcodeCardIds}
                                    onAddNew={() => setIsAdding(true)}
                                    selectedCardIds={selectedCardIds}
                                    onCardSelect={handleCardSelect}
                                    onSelectAll={handleSelectAll}
                                />
                            </div>
                        )}
                    </div>

                    {user && !isLocked && <StoreSummaryTable storeSummary={summary.storeSummary} />}

                    {user && !isLocked && <AnalyticsDashboard cards={giftCards} />}
                </div>
            </div>
        </>
    );
}